import { useState, useEffect, useMemo } from 'react';

import { CalibrationInputFieldConfig, CalibrationPointsConfig } from 'features/calibration/sensors/types';
import { initializeCalibrationDataFields } from '../utils/dataTransformation';
import { getCalibrationFieldConfig } from 'features/calibration/sensors/sensorSchemas';

const DEFAULT_CALIBRATION_POINTS: CalibrationPointsConfig = {
  increasing: [10, 20, 30, 40, 50],
  decreasing: [40, 30, 20, 10, 0],
  secondIncreasing: [10, 20, 30, 40, 50]
};

export interface UseCalibrationFormReturn {
  individualCalibrationData: any;
  setIndividualCalibrationData: React.Dispatch<React.SetStateAction<any>>;
  calibrationFieldConfig: CalibrationInputFieldConfig | null;
  setCalibrationFieldConfig: React.Dispatch<React.SetStateAction<CalibrationInputFieldConfig | null>>;
  schemaConfig: {
    fieldConfig: CalibrationInputFieldConfig | null;
    calibrationPoints: CalibrationPointsConfig;
    generatePlaceholder: (point: number, sequence?: 'inc' | 'dec' | 'inc2') => string;
  };
  clearAllFields: () => void;
}

export const useCalibrationForm = (
  prefillSensorId?: string,
  prefillInputData?: any,
  isOpen?: boolean
) => {
  const [individualCalibrationData, setIndividualCalibrationData] = useState(
    initializeCalibrationDataFields()
  );
  const [calibrationFieldConfig, setCalibrationFieldConfig] = useState<CalibrationInputFieldConfig | null>(null);

  // Schema configuration hook - consolidates all schema-related logic
  const schemaConfig = useMemo(() => {
    const fieldConfig = getCalibrationFieldConfig(
      individualCalibrationData.sensorType || null,
      individualCalibrationData.category || undefined
    );

    const calibrationPoints = fieldConfig?.calibrationPoints || DEFAULT_CALIBRATION_POINTS;

    const generatePlaceholder = fieldConfig?.generatePlaceholder 
      ? ((point: number, sequence?: 'inc' | 'dec' | 'inc2') => {
          // Wrap the original function to make sequence optional
          // The original function requires sequence, so we provide a default
          return fieldConfig.generatePlaceholder!(point, sequence || 'inc');
        })
      : ((point: number, sequence?: 'inc' | 'dec' | 'inc2') => `${1800 + point * 5}`);

    return {
      fieldConfig,
      calibrationPoints,
      generatePlaceholder,
    };
  }, [individualCalibrationData.sensorType, individualCalibrationData.category]);

  // Pre-fill sensor ID when provided
  useEffect(() => {
    if (isOpen && prefillSensorId) {
      setIndividualCalibrationData((prev: any) => ({
        ...prev,
        sensorId: prefillSensorId
      }));
    }
  }, [isOpen, prefillSensorId]);

  // Pre-fill form with existing calibration input_data when provided
  useEffect(() => {
    if (isOpen && prefillInputData) {
      setIndividualCalibrationData((prev: any) => {
        const updatedData: any = { ...prev };
        
        // Pre-fill BOL
        if (prefillInputData.BOL !== undefined) {
          updatedData.BOL = prefillInputData.BOL.toString();
        }
        
        // Pre-fill temperature frequency
        if (prefillInputData.temperature_frequency && Array.isArray(prefillInputData.temperature_frequency)) {
          const roomTemp = prefillInputData.temperature_frequency.find((tf: any) => tf.condition === 'room');
          const highTemp = prefillInputData.temperature_frequency.find((tf: any) => tf.condition === 'high');
          
          if (roomTemp) {
            updatedData.roomT = roomTemp.temperature?.toString() || '';
            updatedData.roomF = roomTemp.frequency?.toString() || '';
          }
          if (highTemp) {
            updatedData.highT = highTemp.temperature?.toString() || '';
            updatedData.highTempF = highTemp.frequency?.toString() || '';
          }
        }
        
        // Pre-fill raw_frequency data into form fields
        if (prefillInputData.raw_frequency && Array.isArray(prefillInputData.raw_frequency)) {
          const rawFreq = prefillInputData.raw_frequency;
          
          // Map displacement/pressure values to form fields
          const isPiezometer = rawFreq[0]?.pressure !== undefined;
          const getValue = (rf: any) => isPiezometer ? rf.pressure : rf.displacement;
          
          // Extract AOL from the first entry at displacement/pressure 0
          // AOL should be the first entry in raw_frequency at displacement 0
          const firstEntry = rawFreq[0];
          if (firstEntry && getValue(firstEntry) === 0 && firstEntry.frequency) {
            updatedData.AOL = firstEntry.frequency.toString();
          }
          
          // Group by cycle type
          const increment = rawFreq.filter((rf: any) => rf.cycle === 'increment' || !rf.cycle);
          const decrement = rawFreq.filter((rf: any) => rf.cycle === 'decrement');
          const increment2 = rawFreq.filter((rf: any, idx: number) => {
            const firstDecIdx = rawFreq.findIndex((r: any) => r.cycle === 'decrement');
            return idx > firstDecIdx && (rf.cycle === 'increment' || !rf.cycle);
          });
          
          // First increment cycle (excluding displacement/pressure 0, which is AOL)
          increment.forEach((rf: any, idx: number) => {
            const value = getValue(rf);
            if (value > 0) {
              updatedData[`point_${value}_inc`] = rf.frequency?.toString() || '';
            }
          });
          
          // Decrement cycle
          decrement.forEach((rf: any) => {
            const value = getValue(rf);
            if (value === 0) {
              updatedData.point_0 = rf.frequency?.toString() || '';
            } else {
              updatedData[`point_${value}_dec`] = rf.frequency?.toString() || '';
            }
          });
          
          // Second increment cycle
          increment2.forEach((rf: any) => {
            const value = getValue(rf);
            if (value > 0) {
              updatedData[`point_${value}_inc2`] = rf.frequency?.toString() || '';
            }
          });
        }
        
        return updatedData;
      });
    }
  }, [isOpen, prefillInputData]);

  const clearAllFields = () => {
    const defaultPoints = schemaConfig.calibrationPoints;
    const currentData = individualCalibrationData;
    const allCalibrationFields: Record<string, string> = {};
    
    // Find all calibration point fields in current data and mark them for clearing
    Object.keys(currentData).forEach(key => {
      if (key.startsWith('point_') || key === 'point_0') {
        allCalibrationFields[key] = "";
      }
    });
    
    const clearedData = initializeCalibrationDataFields(defaultPoints);
    const finalClearedData = {
      ...clearedData,
      ...allCalibrationFields
    };
    
    setIndividualCalibrationData(finalClearedData);
    
    if (calibrationFieldConfig) {
      setCalibrationFieldConfig({
        ...calibrationFieldConfig,
        calibrationPoints: defaultPoints
      });
    }
  };

  return {
    individualCalibrationData,
    setIndividualCalibrationData,
    calibrationFieldConfig,
    setCalibrationFieldConfig,
    schemaConfig,
    clearAllFields,
  };
};

